import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Block,
  Search,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchTenants,
  createTenant,
  updateTenant,
  activateTenant,
  suspendTenant,
  deleteTenant,
  fetchSubscriptionPlans,
} from '../../../store/slices/superAdminSlice';
import {
  TenantDto,
  CreateTenant,
  UpdateTenant,
} from '../../../types/superAdminTypes';
import Swal from 'sweetalert2';
const TenantManagementPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { tenants, subscriptionPlans, loading, error } = useSelector((state: RootState) => state.superAdmin);
  const activeSubscriptionPlans = React.useMemo(
    () => subscriptionPlans.filter((plan) => plan.isActive),
    [subscriptionPlans]
  );
  // Pagination state (ADDED)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Helper function to extract and format error messages
  const extractErrorMessage = (err: any): string => {
    if (typeof err === 'string') {
      return err;
    } else if (err?.message) {
      return err.message;
    } else if (err?.payload) {
      return typeof err.payload === 'string' ? err.payload : err.payload.message || 'Operation failed';
    }
    return 'Operation failed';
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantDto | null>(null);
  const [viewTenant, setViewTenant] = useState<TenantDto | null>(null);
  const [form, setForm] = useState<CreateTenant>({
    companyName: '',
    companyCode: '',
    email: '',
    phone: '',
    address: '',
    country: '',
    subscriptionPlanId: '',
    employeeLimit: 0,
  });
  const [orderBy, setOrderBy] = useState<string>('companyName');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const formatTenantExpiry = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-IN');
  };
  const fetchData = useCallback(() => {
    dispatch(fetchTenants({ searchTerm: searchTerm || undefined }));
  }, [dispatch, searchTerm]);
  useEffect(() => {
    fetchData();
    dispatch(fetchSubscriptionPlans());
  }, [fetchData, dispatch]);
  // Pagination handlers (ADDED)
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // const paginatedTenants = tenants.slice(
  //   page * rowsPerPage,
  //   page * rowsPerPage + rowsPerPage
  // );

  const handleOpenCreate = () => {
    setEditingTenant(null);
    setForm({ companyName: '', companyCode: '', email: '', phone: '', address: '', country: '', subscriptionPlanId: '', employeeLimit: 0 });
    setOpenDialog(true);
  };
  const handleSort = (field: string) => {
  const isAsc = orderBy === field && order === 'asc';
  setOrder(isAsc ? 'desc' : 'asc');
  setOrderBy(field);
};

const sortedTenants = React.useMemo(() => {
  const data = [...tenants];

  return data.sort((a: any, b: any) => {
    const aValue = a?.[orderBy] ?? '';
    const bValue = b?.[orderBy] ?? '';

    // numeric sort
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // string sort fallback
    return order === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });
}, [tenants, orderBy, order]);

  const paginatedTenants = sortedTenants.slice(
  page * rowsPerPage,
  page * rowsPerPage + rowsPerPage
);


  const handleOpenEdit = (tenant: TenantDto) => {
    setEditingTenant(tenant);
    setForm({
      companyName: tenant.companyName,
      companyCode: tenant.companyCode,
      email: tenant.email,
      phone: tenant.phone || '',
      address: tenant.address || '',
      country: tenant.country || '',
      subscriptionPlanId: tenant.subscriptionPlanId || '',
      employeeLimit: tenant.employeeLimit,
    });
    setOpenDialog(true);
  };
  const handleSubmit = async () => {
    try {
      if (!form.companyName.trim() || !form.companyCode.trim() || !form.email.trim()) {
        toast.error('Company Name, Company Code, and Email are required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      const phone = form.phone?.trim();
      if (phone && phone.length < 10) {
        toast.error('Phone number must be at least 10 digits');
        return;
      }
      const normalizedForm: CreateTenant = {
        ...form,
        companyName: form.companyName.trim(),
        companyCode: form.companyCode.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || undefined,
        address: form.address?.trim() || undefined,
        country: form.country?.trim() || undefined,
        subscriptionPlanId:
          form.subscriptionPlanId && activeSubscriptionPlans.some((plan) => plan.id === form.subscriptionPlanId)
            ? form.subscriptionPlanId
            : undefined,
      };
      if (editingTenant) {
        const updateData: UpdateTenant = { ...normalizedForm, id: editingTenant.id };
        await dispatch(updateTenant({ id: editingTenant.id, data: updateData })).unwrap();
        toast.success('Tenant updated successfully');
        setOpenDialog(false);
        fetchData();
        return;
      } else {
        const res = await dispatch(createTenant(normalizedForm)).unwrap();
        let isDuplicate = false;
        if (typeof res === 'object' && res !== null && 'id' in res) {
          if (res.id === 'Email already exists') {
            toast.error(res.id);
            isDuplicate = true;
          } else if (res.id === 'Mobile number already exists') {
            toast.error(res.id);
            isDuplicate = true;
          } else {
            toast.success('Tenant created successfully');
          }
        } else if (typeof res === 'string') {
          if (res === 'Email already exists') {
            toast.error(res);
            isDuplicate = true;
          } else if (res === 'Mobile number already exists') {
            toast.error(res);
            isDuplicate = true;
          } else {
            toast.success('Tenant created successfully');
          }
        } else {
          toast.success('Tenant created successfully');
        }
        if (!isDuplicate) {
          setOpenDialog(false);
          fetchData();
        }
      }
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err).toLowerCase();
      if (errorMessage.includes('email') && (errorMessage.includes('already exist') || errorMessage.includes('already exists'))) {
        toast.error('Email already exists');
      } else if ((errorMessage.includes('phone') || errorMessage.includes('mobile')) && (errorMessage.includes('already exist') || errorMessage.includes('already exists'))) {
        toast.error('Mobile number already exists');
      } else {
        toast.error(extractErrorMessage(err));
      }
    }
  };
  const handleActivate = async (id: string) => {
    try {
      await dispatch(activateTenant(id)).unwrap();
      toast.success('Tenant activated');
      fetchData();
    } catch (err: any) {
      toast.error(extractErrorMessage(err));
    }
  };
  const handleSuspend = async (id: string) => {
    const result = await Swal.fire({
      title: 'Suspend Tenant?',
      text: 'This will temporarily disable access for all users of this tenant.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Yes, Suspend',
    });
    if (result.isConfirmed) {
      try {
        await dispatch(suspendTenant(id)).unwrap();
        toast.success('Tenant suspended');
        fetchData();
      } catch (err: any) {
        toast.error(extractErrorMessage(err));
      }
    }
  };
  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: 'Delete Tenant?',
      text: 'This action cannot be undone. All tenant data will be removed.',
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Yes, Delete',
    });
    if (result.isConfirmed) {
      try {
        await dispatch(deleteTenant(id)).unwrap();
        toast.success('Tenant deleted');
      } catch (err: any) {
        toast.error(extractErrorMessage(err));
      }
    }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Tenant Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage all companies and their subscriptions</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
          Add Tenant
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ endAdornment: <Search color="action" /> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {tenants.length}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {/* <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell> */}
              <TableCell
              sx={{ fontWeight: 'bold', cursor: 'pointer' }}
              onClick={() => handleSort('companyName')}
            >
              Company
            </TableCell>
              {/* <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>*/}
              
              <TableCell
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleSort('companyCode')}
              >
                Code
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleSort('email')}
              >
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell> 
              <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleSort('tenantExpiry')}
              >
                Tenant Expiry
              </TableCell>
              {/* <TableCell sx={{ fontWeight: 'bold' }}>Employees</TableCell> */}
              <TableCell
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleSort('employeeLimit')}
              >
                Employees
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              {/* <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell> */}
              <TableCell
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleSort('createdAt')}
              >
                Created
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 10 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No tenants found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTenants.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{tenant.companyName}</Typography>
                  </TableCell>
                  <TableCell>{tenant.companyCode}</TableCell>
                  <TableCell>{tenant.email}</TableCell>
                  <TableCell>{tenant.phone}</TableCell>
                  <TableCell>{tenant.subscriptionPlanName || '—'}</TableCell>
                  <TableCell>{formatTenantExpiry(tenant.tenantExpiry)}</TableCell>
                  <TableCell>{tenant.currentEmployeeCount} / {tenant.employeeLimit}</TableCell>
                  <TableCell>
                    <Chip label={tenant.isSuspended ? 'Suspended' : tenant.isActive ? 'Active' : 'Inactive'} color={tenant.isSuspended ? 'error' : tenant.isActive ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => setViewTenant(tenant)}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEdit(tenant)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    {(tenant.isSuspended || !tenant.isActive) && (
                      <Tooltip title="Activate">
                        <IconButton size="small" color="success" onClick={() => handleActivate(tenant.id)}><CheckCircle fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {tenant.isActive && !tenant.isSuspended && (
                      <Tooltip title="Suspend">
                        <IconButton size="small" color="warning" onClick={() => handleSuspend(tenant.id)}><Block fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination (ADDED) */}
      <TablePagination
        component="div"
        count={tenants.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingTenant ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Company Name" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Company Code" value={form.companyCode} onChange={(e) => setForm({ ...form, companyCode: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                inputProps={{ maxLength: 10 }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setForm({ ...form, phone: value.slice(0, 10) });
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select value={activeSubscriptionPlans.some((plan) => plan.id === form.subscriptionPlanId) ? form.subscriptionPlanId : ''} label="Subscription Plan" onChange={(e) => {
                  const planId = e.target.value;
                  const selectedPlan = activeSubscriptionPlans.find((p) => p.id === planId);
                  setForm({ ...form, subscriptionPlanId: planId, employeeLimit: selectedPlan?.maxUsers ?? 0 });
                }}>
                  <MenuItem value="">None</MenuItem>
                  {activeSubscriptionPlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.planName} — ₹{plan.pricePerEmployee}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Employee Limit" type="number" value={form.employeeLimit} onChange={(e) => setForm({ ...form, employeeLimit: parseInt(e.target.value) || 0 })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {editingTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!viewTenant} onClose={() => setViewTenant(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Tenant Details</DialogTitle>
        <DialogContent>
          {viewTenant && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                ['Company Name', viewTenant.companyName],
                ['Company Code', viewTenant.companyCode],
                ['Email', viewTenant.email],
                ['Phone', viewTenant.phone || '—'],
                ['Address', viewTenant.address || '—'],
                ['Country', viewTenant.country || '—'],
                ['Plan', viewTenant.subscriptionPlanName || '—'],
                ['Tenant Expiry', formatTenantExpiry(viewTenant.tenantExpiry)],
                ['Employees', `${viewTenant.currentEmployeeCount} / ${viewTenant.employeeLimit}`],
                ['Status', viewTenant.isSuspended ? 'Suspended' : viewTenant.isActive ? 'Active' : 'Inactive'],
                ['Created', new Date(viewTenant.createdAt).toLocaleDateString()],
              ].map(([label, val]) => (
                <Grid size={{ xs: 12, sm: 6 }} key={label as string}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={500}>{val}</Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTenant(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default TenantManagementPage;
